// =============================================================================
// ONGLET DONNÉES MPR - Saisie des données du dossier MaPrimeRénov'
// =============================================================================
// Permet de saisir et stocker les informations spécifiques MPR :
// - Numéro de dossier MPR
// - Données du mandataire
// - Variables pour les documents
// =============================================================================

import { useState, useEffect } from "react";
import {
  FileText, Save, Loader2, CheckCircle2, AlertCircle,
  Info, Edit, Building2, User, Hash, Calendar, Euro
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { toast } from "sonner";
import { formatClientFullName } from "../../utils/clientFormat";
import { formatDateFR } from "../../utils/dateFormat";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Sentinelle « Aucun mandataire » (Radix Select refuse value=""). Mappée vers ""
// → persistée en null. Mandataire = choix libre, jamais de défaut imposé.
const MANDATAIRE_NONE = "__none__";

export default function DonneesMPA({ devisId, devis, dossier, tenantId = "climatpac", isTEF = false, onUpdate, hideMandataire = false }) {
  // V1.5 : AMO/TEF = donnees_mpa (/donnees-mpa, sous-champs *_mpa). Non-TEF
  // (OMV/Climat = MaPrimeRénov', futur module Installateur) reste sur donnees_mpr.
  // L'état interne garde des clés *_mpr (noms JS internes) ; on mappe vers/depuis
  // les clés DB via le suffixe. Lecture double-suffixe = robuste en transition.
  const SFX = isTEF ? "mpa" : "mpr";
  const DM_FIELD = isTEF ? "donnees_mpa" : "donnees_mpr";
  const DM_EP = isTEF ? "donnees-mpa" : "donnees-mpr";
  const readSub = (src, base) => src?.[`${base}_mpa`] ?? src?.[`${base}_mpr`];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [mandataires, setMandataires] = useState([]);
  const [defaultMandataireId, setDefaultMandataireId] = useState(null);
  
  // Données MPR du dossier
  const [donneesMPR, setDonneesMPR] = useState({
    numero_dossier_mpr: "",
    mandataire_id: "",
    date_creation_mpr: "",
    date_depot_mpr: "",
    date_acceptation_mpr: "",
    montant_mpr_estime: "",
    montant_mpr_accorde: "",
    statut_mpr: "non_depose",
    notes_mpr: ""
  });

  // Fonction pour obtenir les headers avec authentification (toujours frais)
  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    if (!tenantId) {
      console.error("[DonneesMPR] tenantId manquant — impossible d'appeler l'API.");
    }
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Charger les données au montage
  useEffect(() => {
    loadData();
  }, [dossier?.id, devisId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les mandataires (capture local du default — la state setter est
      // batchée et ne sera pas lisible avant le prochain render).
      let localDefaultId = null;
      // Liste scopée au tenant DU DOSSIER (getAuthHeaders() sans arg → défaut
      // "climatpac", qui masquait le mandataire tef). Même source que le sélecteur
      // du haut → listes identiques, FK résolu.
      const resMandataires = await fetch(`${API}/api/platform/mandataires`, {
        headers: getAuthHeaders(dossier?.tenant_id || tenantId)
      });
      if (resMandataires.ok) {
        const data = await resMandataires.json();
        setMandataires(data.mandataires || []);
        localDefaultId = data.default_id || null;
        setDefaultMandataireId(localDefaultId);
      }

      // Charger les données MPA/MPR du dossier (si existantes)
      let donneesMPRFromDossier = null;
      if (dossier?.id) {
        const resDossier = await fetch(`${API}/api/metier/dossiers/${dossier.id}`, {
          headers: getAuthHeaders()
        });
        if (resDossier.ok) {
          const dossierData = await resDossier.json();
          const src = dossierData[DM_FIELD] || dossierData.donnees_mpa || dossierData.donnees_mpr;
          if (src) {
            donneesMPRFromDossier = src;
            // Mappe les clés DB (suffixe *_mpa|*_mpr) → état interne (*_mpr).
            // Mandataire = choix LIBRE, jamais de défaut imposé ; vide → « Aucun ».
            setDonneesMPR(prev => ({
              ...prev,
              numero_dossier_mpr: readSub(src, "numero_dossier") ?? "",
              statut_mpr: readSub(src, "statut") ?? "non_depose",
              date_creation_mpr: readSub(src, "date_creation") ?? "",
              date_depot_mpr: readSub(src, "date_depot") ?? "",
              date_acceptation_mpr: readSub(src, "date_acceptation") ?? "",
              montant_mpr_estime: (src.montant_mpa_estime ?? src.montant_mpr_estime) ?? "",
              montant_mpr_accorde: (src.montant_mpa_accorde ?? src.montant_mpr_accorde) ?? "",
              notes_mpr: readSub(src, "notes") ?? "",
              mandataire_id: src.mandataire_id || "",
            }));
          } else {
            // Pas de donnees_mpr → mandataire vide (aucun défaut imposé).
            setDonneesMPR(prev => ({
              ...prev,
              mandataire_id: "",
            }));
          }
        }
      }

      // Pour TEF: si pas de données MPR manuelles, pré-remplir depuis ANAH
      if (isTEF && dossier?.id && !readSub(donneesMPRFromDossier, "numero_dossier")) {
        try {
          const resAnah = await fetch(`${API}/api/metier/dossiers/${dossier.id}/anah-status`, {
            headers: getAuthHeaders()
          });
          if (resAnah.ok) {
            const anahStatus = await resAnah.json();
            if (anahStatus.anah_linked && anahStatus.anah_data) {
              const anah = anahStatus.anah_data;
              // Mapper les statuts ANAH vers les statuts MPA
              const statutMap = {
                "instruction": "en_cours",
                "accorde": "accepte",
                "refuse": "refuse",
                "paye": "paye",
                "déposé": "en_cours",
              };
              const mappedStatut = statutMap[anah.statut_normalise] || "non_depose";
              
              // Convertir date ANAH (DD/MM/YYYY) vers format date input (YYYY-MM-DD)
              let dateDepot = "";
              if (anah.date_depot) {
                const parts = anah.date_depot.split("/");
                if (parts.length === 3) {
                  dateDepot = `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
              }

              setDonneesMPR(prev => ({
                ...prev,
                numero_dossier_mpr: prev.numero_dossier_mpr || anah.reference_anah || "",
                statut_mpr: prev.statut_mpr === "non_depose" ? mappedStatut : prev.statut_mpr,
                date_depot_mpr: prev.date_depot_mpr || dateDepot,
                montant_mpr_accorde: prev.montant_mpr_accorde || (anah.subvention_accordee ? String(anah.subvention_accordee) : ""),
              }));
            }
          }
        } catch (err) {
          console.error("Erreur chargement données ANAH:", err);
        }
      }
    } catch (err) {
      console.error("Erreur chargement données MPR:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Vérifier que le dossier a un ID valide
    const dossierId = dossier?.id || dossier?._id;
    if (!dossierId) {
      toast.error("ID du dossier non disponible");
      console.error("Dossier reçu:", dossier);
      return;
    }
    
    setSaving(true);
    try {
      // Nettoyer + mapper vers les clés DB avec le bon suffixe (mpa TEF / mpr non-TEF).
      const cleanData = {
        [`numero_dossier_${SFX}`]: donneesMPR.numero_dossier_mpr || null,
        mandataire_id: donneesMPR.mandataire_id || null,
        [`date_creation_${SFX}`]: donneesMPR.date_creation_mpr || null,
        [`date_depot_${SFX}`]: donneesMPR.date_depot_mpr || null,
        [`date_acceptation_${SFX}`]: donneesMPR.date_acceptation_mpr || null,
        [`montant_${SFX}_estime`]: donneesMPR.montant_mpr_estime ? parseFloat(donneesMPR.montant_mpr_estime) : null,
        [`montant_${SFX}_accorde`]: donneesMPR.montant_mpr_accorde ? parseFloat(donneesMPR.montant_mpr_accorde) : null,
        [`statut_${SFX}`]: donneesMPR.statut_mpr || "non_depose",
        [`notes_${SFX}`]: donneesMPR.notes_mpr || null
      };

      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/${DM_EP}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanData)
      });

      if (res.ok) {
        toast.success(isTEF ? "Données MPA enregistrées" : "Données MPR enregistrées");
        setEditing(false);
        // Source unique mandataire : prévenir le parent pour que les autres points
        // d'accès (sélecteur mandataire en tête de « Contrats & Engagement ») se
        // resynchronisent sur la même valeur donnees_mpr.mandataire_id.
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la sauvegarde");
      }
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setDonneesMPR(prev => ({ ...prev, [field]: value }));
  };

  // Récupérer le mandataire sélectionné
  // Mandataire affiché = EXACTEMENT celui choisi (donnees_mpr.mandataire_id).
  // Pas de repli sur le défaut : sans choix → undefined → « Aucun ».
  const selectedMandataire = mandataires.find(m => m.id === donneesMPR.mandataire_id);

  // Labels dynamiques selon activité
  const labels = isTEF ? {
    titre: "Données MaPrimeAdapt'",
    sousTitre: "Informations spécifiques au dossier MPA pour la génération des documents",
    dossierTitre: "Dossier ANAH",
    dossierDesc: "Référence et statut du dossier MaPrimeAdapt'",
    numeroDossier: "Numéro de dossier ANAH",
    numeroDossierPlaceholder: "MPA-2026-XXXXXXX",
    statut: "Statut MPA",
    mandataireTitre: "Mandataire ANAH",
    mandataireDesc: "Entreprise mandataire pour ce dossier ANAH",
    mandataireVide: "Rendez-vous dans Commercial Hub → Gérer l'entreprise → Mandataire ANAH pour en créer un.",
    variableInfo: "numero_dossier_mpr",
    couleur: "orange",
  } : {
    titre: "Données MaPrimeRénov'",
    sousTitre: "Informations spécifiques au dossier MPR pour la génération des documents",
    dossierTitre: "Dossier MPR",
    dossierDesc: "Référence et statut du dossier MaPrimeRénov'",
    numeroDossier: "Numéro de dossier MPR",
    numeroDossierPlaceholder: "MPR-2026-XXXXXXX",
    statut: "Statut MPR",
    mandataireTitre: "Mandataire MPR",
    mandataireDesc: "Entreprise mandataire pour ce dossier",
    mandataireVide: "Rendez-vous dans Commercial Hub → Gérer l'entreprise → Mandataire MPR pour en créer un.",
    variableInfo: "numero_dossier_mpr",
    couleur: "violet",
  };

  const STATUTS_MPR = [
    { code: "non_depose", label: "Non déposé", color: "bg-zinc-500/20 border border-[var(--oh-border)]" },
    { code: "en_cours", label: "En cours d'instruction", color: "bg-amber-500" },
    { code: "accepte", label: "Accepté", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
    { code: "refuse", label: "Refusé", color: "bg-red-500" },
    { code: "paye", label: "Prime versée", color: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]" }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
              <FileText className={`w-5 h-5 ${isTEF ? 'text-[var(--oh-info-text)]' : 'text-[var(--oh-accent-text)]'}`} />
              {labels.titre}
            </h2>
            <p className="text-[var(--oh-text-muted)] text-sm mt-1">
              {labels.sousTitre}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!editing ? (
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
                className="border-[var(--oh-border)] text-[var(--oh-text-muted)] hover:bg-[var(--oh-bg-elevated)]"
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    loadData();
                  }}
                  className="border-[var(--oh-border)] text-[var(--oh-text-muted)] hover:bg-[var(--oh-bg-elevated)]"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informations dossier MPR */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader>
              <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                <Hash className={`w-5 h-5 ${isTEF ? 'text-[var(--oh-info-text)]' : 'text-[var(--oh-accent-text)]'}`} />
                {labels.dossierTitre}
              </CardTitle>
              <CardDescription className="text-[var(--oh-text-muted)]">
                {labels.dossierDesc}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-[var(--oh-text-muted)] text-sm">{labels.numeroDossier}</Label>
                {editing ? (
                  <Input
                    value={donneesMPR.numero_dossier_mpr}
                    onChange={(e) => updateField("numero_dossier_mpr", e.target.value)}
                    placeholder={labels.numeroDossierPlaceholder}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                ) : (
                  <p className="text-[var(--oh-text)] font-medium mt-1">
                    {donneesMPR.numero_dossier_mpr || (
                      <span className="text-[var(--oh-text-secondary)] italic">Non renseigné</span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)] text-sm">{labels.statut}</Label>
                {editing ? (
                  <Select
                    value={donneesMPR.statut_mpr}
                    onValueChange={(v) => updateField("statut_mpr", v)}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUTS_MPR.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={`${STATUTS_MPR.find(s => s.code === donneesMPR.statut_mpr)?.color || 'bg-[var(--oh-bg-elevated)]'} text-[var(--oh-text)] mt-1`}>
                    {STATUTS_MPR.find(s => s.code === donneesMPR.statut_mpr)?.label || "Non défini"}
                  </Badge>
                )}
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)] text-sm">Date de création</Label>
                {editing ? (
                  <Input
                    type="date"
                    value={donneesMPR.date_creation_mpr}
                    onChange={(e) => updateField("date_creation_mpr", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                ) : (
                  <p className="text-[var(--oh-text)] mt-1">
                    {donneesMPR.date_creation_mpr ? formatDateFR(donneesMPR.date_creation_mpr) : (
                      <span className="text-[var(--oh-text-secondary)] italic">Non renseigné</span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)] text-sm">Date de dépôt</Label>
                {editing ? (
                  <Input
                    type="date"
                    value={donneesMPR.date_depot_mpr}
                    onChange={(e) => updateField("date_depot_mpr", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                ) : (
                  <p className="text-[var(--oh-text)] mt-1">
                    {donneesMPR.date_depot_mpr ? formatDateFR(donneesMPR.date_depot_mpr) : (
                      <span className="text-[var(--oh-text-secondary)] italic">Non renseigné</span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)] text-sm">Date d'acceptation</Label>
                {editing ? (
                  <Input
                    type="date"
                    value={donneesMPR.date_acceptation_mpr}
                    onChange={(e) => updateField("date_acceptation_mpr", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                ) : (
                  <p className="text-[var(--oh-text)] mt-1">
                    {donneesMPR.date_acceptation_mpr ? formatDateFR(donneesMPR.date_acceptation_mpr) : (
                      <span className="text-[var(--oh-text-secondary)] italic">Non renseigné</span>
                    )}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Montant estimé</Label>
                  {editing ? (
                    <Input
                      type="number"
                      value={donneesMPR.montant_mpr_estime}
                      onChange={(e) => updateField("montant_mpr_estime", e.target.value)}
                      placeholder="0.00"
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  ) : (
                    <p className="text-[var(--oh-text)] font-medium mt-1">
                      {donneesMPR.montant_mpr_estime ? `${Number(donneesMPR.montant_mpr_estime).toLocaleString("fr-FR")} €` : (
                        <span className="text-[var(--oh-text-secondary)] italic">-</span>
                      )}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Montant accordé</Label>
                  {editing ? (
                    <Input
                      type="number"
                      value={donneesMPR.montant_mpr_accorde}
                      onChange={(e) => updateField("montant_mpr_accorde", e.target.value)}
                      placeholder="0.00"
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  ) : (
                    <p className="text-[var(--oh-success-text)] font-medium mt-1">
                      {donneesMPR.montant_mpr_accorde ? `${Number(donneesMPR.montant_mpr_accorde).toLocaleString("fr-FR")} €` : (
                        <span className="text-[var(--oh-text-secondary)] italic">-</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mandataire — RETIRÉ de Conformité (décision CTO : hideMandataire=true depuis
              DossierDetail). Le CHOIX se fait en Demande MPA (liste déroulante,
              CommercialSection) ; la GESTION/référentiel dans Configuration Mandataire ANAH
              (GererEntreprise → MandataireMPR). Conservé pour les autres usages (non-TEF). */}
          {!hideMandataire && (
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader>
              <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[var(--oh-accent-text)]" />
                {labels.mandataireTitre}
              </CardTitle>
              <CardDescription className="text-[var(--oh-text-muted)]">
                {labels.mandataireDesc}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-[var(--oh-text-muted)] text-sm">Sélectionner le mandataire</Label>
                {editing ? (
                  <Select
                    value={donneesMPR.mandataire_id || MANDATAIRE_NONE}
                    onValueChange={(v) => updateField("mandataire_id", v === MANDATAIRE_NONE ? "" : v)}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1">
                      <SelectValue placeholder="Aucun" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* « Aucun » = choix explicite (persiste vide). Aucun défaut imposé. */}
                      <SelectItem value={MANDATAIRE_NONE}>Aucun</SelectItem>
                      {mandataires.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.raison_sociale}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-[var(--oh-text)] font-medium mt-1">
                    {selectedMandataire?.raison_sociale || (
                      <span className="text-[var(--oh-text-secondary)] italic">Aucun mandataire sélectionné</span>
                    )}
                  </p>
                )}
              </div>

              {selectedMandataire && (
                <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[var(--oh-text-muted)]" />
                    <span className="text-[var(--oh-text-muted)]">
                      {formatClientFullName(selectedMandataire)}
                    </span>
                  </div>
                  <div className="text-sm text-[var(--oh-text-muted)]">
                    SIREN: {selectedMandataire.siren}
                  </div>
                  <div className="text-sm text-[var(--oh-text-muted)]">
                    {/* Fix [object Object] : mandataire.adresse peut être dict legacy */}
                    {typeof selectedMandataire.adresse === "string"
                      ? selectedMandataire.adresse
                      : (selectedMandataire.adresse?.rue || "")
                    }, {selectedMandataire.code_postal} {selectedMandataire.ville}
                  </div>
                  {selectedMandataire.email && (
                    <div className="text-sm text-[var(--oh-text-muted)]">
                      {selectedMandataire.email}
                    </div>
                  )}
                </div>
              )}

              {mandataires.length === 0 && (
                <div className="bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)] rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-[var(--oh-info-text)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[var(--oh-info-text)] text-sm font-medium">Aucun mandataire configuré</p>
                      <p className="text-[var(--oh-info-text)] text-xs mt-1">
                        {labels.mandataireVide}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>

        {/* Aide */}
        <div className="bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[var(--oh-accent-text)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[var(--oh-accent-text)] text-sm font-medium">Variables utilisées dans les documents</p>
              <p className="text-[var(--oh-accent-text)] text-xs mt-1">
                Les données saisies ici sont utilisées comme variables dans les templates de documents. 
                Par exemple, le numéro de dossier sera injecté dans les mandats et attestations via la variable 
                <code className="bg-[var(--oh-accent-bg)] px-1 rounded mx-1">{`{{${labels.variableInfo}}}`}</code>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
